import { encodeFragment } from "@norkron/web-docz";
import type { MentionRef } from "../../api/types";
import { markdownFragment } from "../../lib/markdownFragment";
import { CommentCanvasBody } from "./CommentCanvasBody";

/**
 * Renders a comment body, dispatching on `body_format` (rich-comments §5.1):
 *
 *  · **`docz-fragment`** → the rich body, painted on the CANVAS DOCUMENT ENGINE
 *    (`<CommentCanvasBody>`). Owner decision **D-E**
 *    (`.claude/plans/canvas-unification.md`): *no DOM renders any docz IR
 *    anywhere.* A `docz-fragment` body is real docz IR — runs and typed marks
 *    lowered through the SAME `html_writer` as the document — so it is a
 *    mini-document, and it now paints through the SAME `pl_layout` pass that
 *    produces the PDF and drives read mode. The owner was offered the option of
 *    keeping comment prose on the DOM and explicitly overrode it; there is
 *    deliberately no DOM painter fallback on this path.
 *
 *    WHAT CHANGED, precisely: this file used to render that IR to HTML, sanitize
 *    it with `sanitizeCommentHtml` and hand it to `dangerouslySetInnerHTML`,
 *    then walk the injected DOM to chip-ify `@Name` spans. There is no
 *    `dangerouslySetInnerHTML` left here. The sanitizer boundary did NOT move —
 *    it now guards the accessible mirror's markup instead of the painted
 *    surface's (see `commentSurfaceModel.commentMirrorBlocks`), and the painted
 *    glyphs come from the IR through wasm with no HTML in the path at all.
 *
 *  · **anything else (legacy `markdown`)** → CONVERTED to that same single-block
 *    fragment (`markdownFragment`) and painted by the SAME surface.
 *
 * ## Why the markdown branch is gone (owner, 2026-07-30)
 *
 * *"why the fuck are review note replies STILL in DOM/HTML?!"* — and they were,
 * but not for the reason the wording suggests. Nothing about a REPLY was
 * different: both the root and the reply arms of `<CommentThread>` render this
 * component with the same props. What differed was the STORED FORMAT. MEASURED on
 * production `a9c50c0`, doc 11: `reply-11` ("P0-FIX reply 1784833740414") is
 * `body_format: "markdown"` and reported no `data-comment-surface` at all, while
 * `reply-135/136/137` beside it reported `canvas`. Two ROOTS in the same document
 * are markdown and were equally DOM.
 *
 * So the dispatch itself was the bug: it made the painter a property of when a
 * note was written. There is now ONE painter for every comment body in the
 * product — a legacy body is converted to IR (`lib/markdownFragment`), which is
 * the same direction `CommentComposer.seedFragment` has always taken when an
 * author edits a legacy note, only preserving `**bold**` as a real docz mark
 * instead of flattening it.
 *
 * ⛔ There is deliberately NO markdown fallback left. A second painter is a
 * second set of type metrics, a second wrap, a second accessibility story and a
 * second place for a bug to hide — which is exactly how this one hid.
 */
export function CommentBody({
  body,
  body_format,
  mentions,
  label,
  lang,
}: {
  body: string;
  body_format?: string;
  mentions: MentionRef[];
  /** Accessible name for the rich body's document landmark (usually the
   *  author's name) — optional so every existing call site is unchanged. */
  label?: string;
  /** BCP-47 language of the comment (WCAG 3.1.2). */
  lang?: string;
}) {
  // A stored `docz-fragment` body IS the transport form; anything else is legacy
  // markdown and is converted to it. Encoding rather than passing the fragment
  // object keeps `<CommentCanvasBody>`'s prop contract a single stable string —
  // its decode is memoised on that string, so a re-render of the card does not
  // rebuild the fragment and remount the surface.
  const json =
    body_format === "docz-fragment" ? body : encodeFragment(markdownFragment(body));
  return (
    <CommentCanvasBody
      json={json}
      mentions={mentions}
      {...(label !== undefined ? { label } : {})}
      {...(lang !== undefined ? { lang } : {})}
    />
  );
}
